/* eslint-disable import/no-internal-modules */
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import { Dictionary } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import { Account } from 'api/account';
import { Project, ProjectFinancialsApiData } from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import {
  ProjectFinancialsContext,
  ProjectFinancialsContextProps,
} from 'contexts/project-financials';
import { useLocalStorage } from 'hooks/use-local-storage';
import {
  FinancialsContentWrapper,
  FinancialsWrapper,
} from './components/common';
import { ProjectFinancialsActionBar } from './components/rows/action-bar';
import { ProjectFinancialsBarCharts } from './components/rows/bar-charts';
import { ProjectFinancialsLineCharts } from './components/rows/line-charts';
import { ProjectFinancialsTables } from './components/rows/tables';
import { ProjectFinancialsWidgets } from './components/rows/widgets';
import { useProjectFinancialsLabels } from './hooks/labels';
import { ProjectFinancialsVisibilityConfig } from './types';
import { projectFinancialsTypeZoomLevelMap } from './utils/common';

interface Props {
  account: Account;
  project: Project;
  projectFinancials: ProjectFinancialsApiData;
  canAccessProfitability: boolean;
  taskStatusOptionsLookup: Dictionary<StatusFieldOption>;
}

highchartsMore(Highcharts);
solidGauge(Highcharts);

export const ProjectFinancials: FC<Props> = ({
  account,
  project,
  projectFinancials,
  canAccessProfitability,
  taskStatusOptionsLookup,
}) => {
  const {
    projectFinancialDetails: { financialContractType },
  } = projectFinancials;

  const allowedZoomLevels = useMemo(
    () => projectFinancialsTypeZoomLevelMap[financialContractType],
    [financialContractType],
  );

  const [
    projectFinancialsVisibilityConfig,
    setProjectFinancialsVisibilityConfig,
  ] = useLocalStorage<ProjectFinancialsVisibilityConfig>(
    'project_financials.visibility',
    {
      profitabilityData: canAccessProfitability,
      allocationData: true,
    },
  );

  const {
    projectBudgetLabel,
    projectBudgetRemainingLabel,
    revenueLabel,
    estimatedRevenueLabel,
    costLabel,
    estimatedCostLabel,
    profitLabel,
    estimatedProfitLabel,
    marginLabel,
    estimatedMarginLabel,
    projectHoursLabel,
    projectHoursRemainingLabel,
    timeAllocatedLabel,
    timeTrackedLabel,
    billableTimeTrackedLabel,
    nonBillableTimeTrackedLabel,
  } = useProjectFinancialsLabels({ financialContractType });

  const revenueLabels = useMemo(
    () => [
      projectBudgetLabel,
      projectBudgetRemainingLabel,
      revenueLabel,
      estimatedRevenueLabel,
      costLabel,
      estimatedCostLabel,
      profitLabel,
      estimatedProfitLabel,
    ],
    [
      costLabel,
      estimatedCostLabel,
      estimatedProfitLabel,
      estimatedRevenueLabel,
      profitLabel,
      projectBudgetLabel,
      projectBudgetRemainingLabel,
      revenueLabel,
    ],
  );

  const timeLabels = useMemo(
    () => [
      projectHoursLabel,
      projectHoursRemainingLabel,
      timeAllocatedLabel,
      timeTrackedLabel,
      billableTimeTrackedLabel,
      nonBillableTimeTrackedLabel,
    ],
    [
      billableTimeTrackedLabel,
      nonBillableTimeTrackedLabel,
      projectHoursLabel,
      projectHoursRemainingLabel,
      timeAllocatedLabel,
      timeTrackedLabel,
    ],
  );

  const percentageLabels = useMemo(
    () => [marginLabel, estimatedMarginLabel],
    [estimatedMarginLabel, marginLabel],
  );

  const toggleVisibility = useCallback(
    (key: string) => {
      setProjectFinancialsVisibilityConfig((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    },
    [setProjectFinancialsVisibilityConfig],
  );

  const contextProps: ProjectFinancialsContextProps = useMemo(
    () => ({
      currencyCode:
        project.currency?.currencyCode ?? account.currency.currencyCode,
      project,
      projectFinancials,
      projectFinancialsVisibilityConfig,
      allowedZoomLevels,
      permissionsMap: {
        projectProfitability: { canRead: canAccessProfitability },
      },
      revenueLabels,
      timeLabels,
      percentageLabels,
    }),
    [
      account.currency.currencyCode,
      allowedZoomLevels,
      canAccessProfitability,
      percentageLabels,
      project,
      projectFinancials,
      projectFinancialsVisibilityConfig,
      revenueLabels,
      timeLabels,
    ],
  );

  return (
    <ProjectFinancialsContext.Provider value={contextProps}>
      <FinancialsWrapper>
        <ProjectFinancialsActionBar toggleVisibility={toggleVisibility} />
        <FinancialsContentWrapper>
          <ProjectFinancialsWidgets />
          <ProjectFinancialsLineCharts />
          <ProjectFinancialsBarCharts />
          <ProjectFinancialsTables
            taskStatusOptionsLookup={taskStatusOptionsLookup}
          />
        </FinancialsContentWrapper>
      </FinancialsWrapper>
    </ProjectFinancialsContext.Provider>
  );
};
